import { useEffect, useState } from 'react';
import {
  getCandidatos, createCandidato, updateCandidato,
  updateEstatusCandidato, deleteCandidato, getUsuarios, createCita,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/ui/Modal';

const ESTATUS_LIST = ['Nuevo','Contactado','Entrevistado','Evaluado','Contratado','Rechazado'];
const TIPOS_CITA = ['PrimerContacto','Seguimiento','Cierre','Otro'];
const EMPTY_CITA_FORM = { titulo: '', fechaHora: '', tipo: 'PrimerContacto', notas: '', lugar: '' };

const ESTATUS_COLORS: Record<string, string> = {
  Nuevo:        'bg-gray-100 text-gray-600',
  Contactado:   'bg-blue-100 text-blue-700',
  Entrevistado: 'bg-yellow-100 text-yellow-700',
  Evaluado:     'bg-orange-100 text-orange-700',
  Contratado:   'bg-green-100 text-green-700',
  Rechazado:    'bg-red-100 text-red-700',
};

const EMPTY_FORM = {
  nombre: '', telefono: '', email: '', notas: '', fechaProximoContacto: '',
};

export default function Reclutamiento() {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'Admin' || user?.rol === 'SuperAdmin';
  const canFilterByAgent = isAdmin || user?.rol === 'Gerente';

  const [candidatos, setCandidatos] = useState<any[]>([]);
  const [agentes, setAgentes] = useState<any[]>([]);
  const [filtros, setFiltros] = useState({ estatus: '', agenteId: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [citaCandidato, setCitaCandidato] = useState<any | null>(null);
  const [citaForm, setCitaForm] = useState(EMPTY_CITA_FORM);
  const [savingCita, setSavingCita] = useState(false);
  const [citaError, setCitaError] = useState('');

  const load = (f = filtros) => {
    const params: any = {};
    if (f.estatus) params.estatus = f.estatus;
    if (f.agenteId) params.agenteId = Number(f.agenteId);
    getCandidatos(params).then(res => setCandidatos(res.data));
  };

  useEffect(() => {
    if (canFilterByAgent) getUsuarios().then(res => setAgentes(res.data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(filtros); }, [filtros]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (c: any) => {
    setEditingId(c.id);
    setForm({
      nombre: c.nombre || '',
      telefono: c.telefono || '',
      email: c.email || '',
      notas: c.notas || '',
      fechaProximoContacto: c.fechaProximoContacto
        ? new Date(c.fechaProximoContacto).toISOString().slice(0, 16)
        : '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      fechaProximoContacto: form.fechaProximoContacto || null,
    };
    try {
      if (editingId != null) {
        const current = candidatos.find(c => c.id === editingId);
        await updateCandidato(editingId, { ...payload, llamado: current?.llamado ?? false });
      } else {
        await createCandidato(payload);
      }
      setShowModal(false);
      load();
    } catch {
      setError('Error al guardar. Verifica los datos.');
    } finally {
      setSaving(false);
    }
  };

  const toggleLlamado = async (c: any) => {
    await updateCandidato(c.id, {
      nombre: c.nombre,
      telefono: c.telefono,
      email: c.email,
      notas: c.notas,
      llamado: !c.llamado,
      fechaProximoContacto: c.fechaProximoContacto,
    });
    setCandidatos(prev => prev.map(x => x.id === c.id ? { ...x, llamado: !x.llamado } : x));
  };

  const handleEstatus = async (id: number, estatus: string) => {
    await updateEstatusCandidato(id, estatus);
    setCandidatos(prev => prev.map(c => c.id === id ? { ...c, estatus } : c));
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este candidato?')) return;
    await deleteCandidato(id);
    setCandidatos(prev => prev.filter(c => c.id !== id));
  };

  const openCita = (c: any) => {
    setCitaCandidato(c);
    setCitaForm({ ...EMPTY_CITA_FORM, titulo: `Cita con ${c.nombre}` });
    setCitaError('');
  };

  const handleAgendarCita = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingCita(true);
    setCitaError('');
    try {
      await createCita({
        titulo: citaForm.titulo,
        fechaHora: citaForm.fechaHora,
        duracionMinutos: 60,
        tipo: citaForm.tipo,
        notas: citaForm.notas || null,
        lugar: citaForm.lugar || null,
        candidatoId: citaCandidato.id,
        todoElDia: false,
      });
      setCitaCandidato(null);
    } catch {
      setCitaError('Error al agendar la cita.');
    } finally {
      setSavingCita(false);
    }
  };

  const field = (key: keyof typeof EMPTY_FORM) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Reclutamiento</h2>
        <button
          onClick={openCreate}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700"
        >
          + Nuevo candidato
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={filtros.estatus}
          onChange={e => setFiltros(f => ({ ...f, estatus: e.target.value }))}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Todos los estatus</option>
          {ESTATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {canFilterByAgent && agentes.length > 0 && (
          <select
            value={filtros.agenteId}
            onChange={e => setFiltros(f => ({ ...f, agenteId: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Todos los agentes</option>
            {agentes.map((a: any) => (
              <option key={a.id} value={a.id}>{a.nombre}</option>
            ))}
          </select>
        )}
        {(filtros.estatus || filtros.agenteId) && (
          <button
            onClick={() => setFiltros({ estatus: '', agenteId: '' })}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">Teléfono</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-center">Llamado</th>
              <th className="px-4 py-3 text-left">Estatus</th>
              <th className="px-4 py-3 text-left">Próximo contacto</th>
              <th className="px-4 py-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {candidatos.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{c.nombre}</td>
                <td className="px-4 py-3 text-gray-600">{c.telefono}</td>
                <td className="px-4 py-3 text-gray-600">{c.email || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => toggleLlamado(c)}
                    title={c.llamado ? 'Marcar como no llamado' : 'Marcar como llamado'}
                    className={`w-7 h-7 rounded-full text-sm font-bold transition-colors ${
                      c.llamado
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                  >
                    {c.llamado ? '✓' : '✗'}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={c.estatus}
                    onChange={e => handleEstatus(c.id, e.target.value)}
                    className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${ESTATUS_COLORS[c.estatus] ?? 'bg-gray-100 text-gray-600'}`}
                  >
                    {ESTATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {c.fechaProximoContacto
                    ? new Date(c.fechaProximoContacto).toLocaleDateString('es-MX')
                    : '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openEdit(c)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => openCita(c)}
                      className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Agendar cita
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(c.id)}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Eliminar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {candidatos.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No hay candidatos registrados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {citaCandidato && (
        <Modal title={`Agendar cita — ${citaCandidato.nombre}`} onClose={() => setCitaCandidato(null)}>
          <form onSubmit={handleAgendarCita} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input type="text" value={citaForm.titulo}
                  onChange={e => setCitaForm(f => ({ ...f, titulo: e.target.value }))}
                  required className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                <select value={citaForm.tipo}
                  onChange={e => setCitaForm(f => ({ ...f, tipo: e.target.value }))}
                  required className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {TIPOS_CITA.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha y hora *</label>
                <input type="datetime-local" value={citaForm.fechaHora}
                  onChange={e => setCitaForm(f => ({ ...f, fechaHora: e.target.value }))}
                  required className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lugar</label>
                <input type="text" value={citaForm.lugar}
                  onChange={e => setCitaForm(f => ({ ...f, lugar: e.target.value }))}
                  placeholder="Oficina, videollamada..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea value={citaForm.notas}
                onChange={e => setCitaForm(f => ({ ...f, notas: e.target.value }))}
                rows={2} placeholder="Observaciones..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>
            {citaError && <p className="text-red-500 text-sm">{citaError}</p>}
            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setCitaCandidato(null)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={savingCita}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60">
                {savingCita ? 'Guardando...' : 'Agendar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showModal && (
        <Modal
          title={editingId != null ? 'Editar candidato' : 'Nuevo candidato'}
          onClose={() => setShowModal(false)}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input type="text" {...field('nombre')} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
                <input type="tel" {...field('telefono')} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" {...field('email')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Próximo contacto</label>
                <input type="datetime-local" {...field('fechaProximoContacto')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea {...field('notas')} rows={3} placeholder="Observaciones..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Guardando...' : editingId != null ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
